import type {
  ProductPaymentMethod,
  UpdateProductPaymentGatewayPayload,
} from '@/services';
import { FINANCE_METHOD_LABELS } from '@/shared/utils/finance-method-label.util';
import {
  CARD_MODE_LABELS,
  GATEWAY_METHOD_ORDER,
  PIX_MODE_LABELS,
  billingMethodLabel,
} from './payment-settings-labels.util';

/** Uma linha do "de/para" — o mesmo formato que a auditoria grava. */
export type SettingsChange = {
  label: string;
  before?: string;
  after?: string;
};

export type GatewaySettingsValues = Omit<
  UpdateProductPaymentGatewayPayload,
  'version'
>;

const yesNo = (value: boolean): string => (value ? 'Sim' : 'Não');

/**
 * "De/para" da cobrança pelo gateway, com os mesmos rótulos e valores que o BFF
 * grava na auditoria (`diffGatewaySettings`): o que a confirmação mostra é o que
 * o histórico vai mostrar. Vazio quando nada mudou.
 */
export function diffGatewaySettings(
  before: GatewaySettingsValues,
  after: GatewaySettingsValues
): SettingsChange[] {
  const changes: SettingsChange[] = [];
  const push = (label: string, from: string, to: string) => {
    if (from !== to) changes.push({ label, before: from, after: to });
  };

  push(
    'Cobrança pelo gateway',
    before.enabled ? 'Ligada' : 'Desligada',
    after.enabled ? 'Ligada' : 'Desligada'
  );
  for (const method of GATEWAY_METHOD_ORDER) {
    push(
      `Oferecer ${FINANCE_METHOD_LABELS[method]}`,
      yesNo(before.enabledMethods.includes(method)),
      yesNo(after.enabledMethods.includes(method))
    );
  }
  push(
    'Cartão parcelado',
    CARD_MODE_LABELS[before.cardBillingMode],
    CARD_MODE_LABELS[after.cardBillingMode]
  );
  push(
    'PIX parcelado',
    PIX_MODE_LABELS[before.pixBillingMode],
    PIX_MODE_LABELS[after.pixBillingMode]
  );
  push(
    'Antecedência do link',
    `${before.chargeLeadDays} dias`,
    `${after.chargeLeadDays} dias`
  );
  push(
    'Dias extras até o link expirar',
    String(before.expireGraceDays),
    String(after.expireGraceDays)
  );
  push(
    'E-mail ao segurado',
    yesNo(before.notifyCustomerByEmail),
    yesNo(after.notifyCustomerByEmail)
  );
  push(
    'Página de retorno',
    before.redirectUrl || '—',
    after.redirectUrl || '—'
  );

  return changes;
}

type MethodRowValues = Pick<
  ProductPaymentMethod,
  'code' | 'description' | 'billingMethod' | 'bankNumber' | 'status'
>;

/** "De/para" de uma forma do catálogo, com os rótulos do BFF. Sem `before`, é a criação. */
export function diffPaymentMethodRow(
  before: MethodRowValues | undefined,
  after: MethodRowValues
): SettingsChange[] {
  if (!before) {
    return [
      {
        label: `Forma ${after.code}`,
        before: '—',
        after: `adicionada: ${describeMethodRow(after)}`,
      },
    ];
  }

  const changes: SettingsChange[] = [];
  const push = (field: string, from: string, to: string) => {
    if (from !== to)
      changes.push({
        label: `Forma ${after.code} · ${field}`,
        before: from,
        after: to,
      });
  };
  const bank = (value?: number) =>
    value === undefined || value === null ? '—' : String(value);

  push('descrição', before.description, after.description);
  push(
    'uso no gateway',
    billingMethodLabel(before.billingMethod),
    billingMethodLabel(after.billingMethod)
  );
  push('banco', bank(before.bankNumber), bank(after.bankNumber));
  push('status', statusLabel(before.status), statusLabel(after.status));

  return changes;
}

function statusLabel(status: ProductPaymentMethod['status']): string {
  return status === 'INACTIVE' ? 'Inativa' : 'Ativa';
}

function describeMethodRow(row: MethodRowValues): string {
  return [
    row.description,
    billingMethodLabel(row.billingMethod),
    row.bankNumber !== undefined && row.bankNumber !== null
      ? `banco ${row.bankNumber}`
      : undefined,
    row.status === 'INACTIVE' ? 'inativa' : 'ativa',
  ]
    .filter(Boolean)
    .join(' · ');
}
